import React, { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import { getThemeConfig } from '../theme';
import { useOrg } from './OrgContext';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');
  const [customColors, setCustomColors] = useState({});
  // A partner's brand colors are the base palette; a user's own theme colors still win over them.
  // White Knight keeps the stock palette exactly as before.
  const { isPlatform, colors: orgColors } = useOrg();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const res = await axios.get('/api/dashboard-settings', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.darkMode) {
            setMode('dark');
          }
          // Load custom theme colors if they exist
          if (res.data.themeColors) {
            setCustomColors(res.data.themeColors);
          }
        }
      } catch (err) {
        console.error('Error fetching theme settings:', err);
      }
    };

    fetchSettings();
  }, []);

  const theme = useMemo(() => {
    const base = isPlatform ? {} : { primary: orgColors.primary, secondary: orgColors.secondary };
    // Every user's saved theme carries the stock palette by default; only colors the user actually
    // changed should beat the company's. Stock values are dropped before merging.
    const stock = mode === 'dark'
      ? { primary: '#90caf9', secondary: '#f48fb1', background: '#121212', paper: '#1e1e1e', surface: '#1e1e1e', text: '#ffffff' }
      : { primary: '#1976d2', secondary: '#dc004e', background: '#ffffff', paper: '#ffffff', surface: '#f5f5f5', text: '#000000' };
    const own = Object.fromEntries(Object.entries(customColors[mode] || {})
      .filter(([k, v]) => v && String(v).toLowerCase() !== String(stock[k] || '').toLowerCase()));
    return createTheme(getThemeConfig(mode, { ...base, ...own }));
  }, [mode, customColors, isPlatform, orgColors]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, customColors, setCustomColors }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
